<script setup lang="ts">
import type { InverterSubmission } from '../types/inverterSubmission'
import { SolButton, SolDivider, SolSelect } from '@solfacil/girassol'
import { toTypedSchema } from '@vee-validate/zod'
import { Field, useFieldArray, useForm } from 'vee-validate'
import { watch } from 'vue'
import { computed } from 'vue'
import * as z from 'zod'
import AddIcon from '~icons/material-symbols/add'
import DeleteIcon from '~icons/material-symbols/delete-outline-rounded'
import InputSystemPower from '~/components/input/SystemPower.vue'
import Tooltip from '~/components/tooltip/index.vue'
import { FEATURE_FLAGS } from '~/constants'
import { useFeatureFlag } from '~/store-v2/feature-flag/useFeatureFlag'
import useHardwareForm, { type UseHardwareFormData } from '~/store-v2/hardware/useHardwareForm'
import useInstallationPowerInvertersValidate from '~/store-v2/installation/useInstallationPowerInvertersValidate'
import useInvertersBrands from '~/store-v2/installation/useInvertersBrands'
import { useInstallationPendingEquipment } from '../composables/useInstallationPendingEquipment'
import usePlantName from '../composables/usePlantName'
import { getPortalNameByBrand } from '../schemas/portals'
import { mapHardwareFormToInstallationDefaults } from '../utils/mapHardwareFormToInstallationDefaults'

// ===========================
// COMPOSABLES & SETUP
// ===========================

const props = defineProps<{
  initialSubmission?: InverterSubmission | null
}>()

const emit = defineEmits<{
  submit: [payload: InverterSubmission]
}>()

const { t } = useI18n()
const route = useRoute('/installation/[id]')
const projectId = computed(() => route.params.id)
const { hasPendingTAF } = useInstallationPendingEquipment(projectId)
const { setPlantName, getPlantName } = usePlantName(projectId)

// ===========================
// SCHEMAS
// ===========================

const validationSchema = z.object({
  systemPower: z.number({ message: 'Potência nominal obrigatória' })
    .min(1, 'Potência nominal obrigatória'),
  brand: z.string({ message: 'Marca obrigatória' })
    .min(1, 'Marca obrigatória'),
  plantName: z.string({ message: 'Nome da planta obrigatório' })
    .min(1, 'Nome da planta obrigatório')
    .max(100, 'Nome da planta deve ter no máximo 100 caracteres'),
  inverters: z.array(
    z.object({
      serialNumber: z.string({ message: 'Número de série obrigatório' })
        .trim()
        .min(3, 'Número de série deve ter no mínimo 3 caracteres')
        .max(30, 'Número de série deve ter no máximo 30 caracteres'),
    }),
  ).min(1, 'Adicione pelo menos um inversor'),
})

// ===========================
// TYPES & INTERFACES
// ===========================

type InverterFormValues = z.infer<typeof validationSchema>

// ===========================
// CONSTANTS
// ===========================

const EMPTY_INVERTER: InverterFormValues['inverters'][number] = {
  serialNumber: '',
}

const isBrandAutoFilled = ref(false)
const didHydrateSystemPower = ref(false)
const isValidatingSubmit = ref(false)

// ===========================
// MUTATIONS & QUERIES
// ===========================

const {
  data: hardwareForm,
  isLoading: isLoadingHardwareForm,
} = useHardwareForm(projectId)

const {
  data: invertersBrands,
  isLoading: isLoadingInvertersBrands,
} = useInvertersBrands()

const { data: canChangeBrand } = useFeatureFlag<boolean>(FEATURE_FLAGS.CHANGE_BRAND_IN_MANUAL_INPUT, false)

// ===========================
// FORM SETUP
// ===========================

const form = useForm({
  validationSchema: toTypedSchema(validationSchema),
  validateOnMount: false,
  initialValues: {
    systemPower: 0,
    plantName: getPlantName() ?? '',
    inverters: [EMPTY_INVERTER],
  },
})

const [systemPowerModel, systemPowerProps] = form.defineField('systemPower')
const [plantNameModel, plantNameProps] = form.defineField('plantName')
const {
  fields: invertersModel,
  remove: removeInverter,
  push: pushInverter,
  replace: replaceInverter,
} = useFieldArray('inverters')

const {
  data: totalPowerValidation,
  isError: isTotalPowerValidationError,
} = useInstallationPowerInvertersValidate(projectId, systemPowerModel)

// ===========================
// COMPUTED
// ===========================

const errors = computed(() => form.errors.value)
const isPending = computed(() => isValidatingSubmit.value)
const hasValidForm = computed(() => form.meta.value.valid)

const brandsOptions = computed(() => (invertersBrands.value ?? []).map(brand => ({
  name: brand.toUpperCase().trim(),
  value: brand.trim(),
})))

const isBrandSelected = computed(() => {
  const selectedBrand = form.values.brand
  return selectedBrand && selectedBrand.trim().length > 0
})

const portalName = computed(() => getPortalNameByBrand(form.values.brand))

const isSubmitDisabled = computed(() =>
  !hasValidForm.value
  || isPending.value
  || totalPowerValidation.value?.is_valid === false
  || !isBrandSelected.value,
)

const isBrandSelectLoading = computed(() => {
  const loading = isLoadingInvertersBrands.value
    || isLoadingHardwareForm.value

  const hasNoBrandsOptions = !loading && (!brandsOptions.value || brandsOptions.value.length === 0)

  return loading || hasNoBrandsOptions
})

const systemPowerError = computed(() => {
  if (isTotalPowerValidationError.value) return 'Erro ao validar a potência nominal total dos inversores instalados.'
  if (!totalPowerValidation.value) return undefined
  if (totalPowerValidation.value?.is_valid) return undefined
  return t(
    `inversorInstallation.form.validatePower.${totalPowerValidation?.value?.error}.title`,
    `inversorInstallation.form.validatePower.defaultError`,
  )
})

// ===========================
// FORM FUNCTIONS
// ===========================

function applySubmissionToForm(submission: InverterSubmission): void {
  form.setFieldValue('systemPower', submission.systemPower, false)
  form.setFieldValue('brand', submission.brand, false)
  form.setFieldValue('plantName', submission.plantName, false)
  replaceInverter(submission.inverters.map(inv => ({
    serialNumber: inv.serialNumber,
  })))
  isBrandAutoFilled.value = false
}

function handleInitialHardwareDefaults(
  [_hardwareForm, _brandsOptions]: [
    UseHardwareFormData | undefined,
    typeof brandsOptions.value | undefined,
  ],
): void {
  if (!_hardwareForm || !_brandsOptions) {
    return
  }

  const defaults = mapHardwareFormToInstallationDefaults(_hardwareForm)

  if (!didHydrateSystemPower.value && defaults.systemPowerKw > 0) {
    form.setFieldValue('systemPower', defaults.systemPowerKw, false)
    didHydrateSystemPower.value = true
  }

  if (form.values.brand || !defaults.brand) {
    return
  }

  const inOption = _brandsOptions.find(brand =>
    brand.value.toLocaleLowerCase().trim() === defaults.brand,
  )
  if (!inOption) {
    return
  }

  form.setFieldValue('brand', defaults.brand, false)
  if (!canChangeBrand.value) {
    isBrandAutoFilled.value = true
  }
}

function handleBrandChange(selectedBrand: any): void {
  form.setFieldValue('brand', selectedBrand.value as never, true)
  if (!canChangeBrand.value) {
    isBrandAutoFilled.value = false
  }
}

function handleSubmit(): void {
  isValidatingSubmit.value = true

  form.handleSubmit((values) => {
    setPlantName(values.plantName)
    emit('submit', {
      systemPower: values.systemPower,
      brand: values.brand,
      plantName: values.plantName,
      inverters: values.inverters,
    })
    isValidatingSubmit.value = false
  }, () => {
    isValidatingSubmit.value = false
  })()
}

// ===========================
// WATCHERS
// ===========================

watch([hardwareForm, brandsOptions], ([hardware, brands]) => {
  handleInitialHardwareDefaults([hardware, brands])
}, { immediate: true, deep: true })

watch(() => props.initialSubmission, (submission) => {
  if (submission) {
    applySubmissionToForm(submission)
  }
}, { immediate: true })
</script>

<template>
  <div class="inverter-upload-mode">
    <div class="form-container">
      <form class="form">
        <InputSystemPower
          id="systemPower"
          v-model="systemPowerModel"
          v-bind="systemPowerProps"
          name="systemPower"
          size="medium"
          :label="t('inversorInstallation.form.validatePower.nominalPowerLabel')"
          placeholder="0,00"
          unit="kW"
          :error="errors.systemPower ?? systemPowerError ?? undefined"
          :disabled="isPending"
        />

        <div class="brand-select-container">
          <SolSelect
            id="inverter-brand"
            :selected="brandsOptions.find(brand => brand.value === form.values.brand)"
            :options="brandsOptions"
            name="brand"
            label="Marca"
            :error="errors.brand"
            :class="{
              error: !!errors.brand,
            }"
            :loading="isBrandSelectLoading"
            :disabled="(!canChangeBrand && isBrandAutoFilled) || isBrandSelectLoading"
            @update:selected="handleBrandChange"
          />

          <div v-if="isBrandAutoFilled && !canChangeBrand" class="brand-disclaimer">
            <span class="brand-disclaimer-text">
              Não é esta marca?
              <a
                :href="`/customer/${projectId}`"
                class="brand-disclaimer-link"
              >
                Faça a alteração do equipamento
              </a>
              .
            </span>
          </div>
        </div>

        <SolInputText
          id="plantName"
          v-model="plantNameModel"
          v-bind="plantNameProps"
          name="plantName"
          size="medium"
          label="Nome da planta de monitoramento"
          placeholder="Digite o nome da planta cadastrada"
          :helper-text="`Digite o nome cadastrado na plataforma ${portalName}`"
          :error="errors.plantName"
          :class="!!errors.plantName ? 'error' : ''"
          :disabled="isPending"
        />

        <div
          v-for="(inverterModel, index) in invertersModel"
          :key="index"
          class="inverter-item"
        >
          <div v-if="invertersModel && invertersModel?.length > 1" class="inverter-item-header">
            <SolDivider thickness="x-small" orientation="horizontal" class="md:site:hidden" />
            <span class="inverter-item-title">
              {{ t('inversorInstallation.form.inverterDataTitle', { index: index + 1 }) }}
            </span>
            <SolDivider thickness="x-small" orientation="horizontal" />
          </div>

          <div class="w-full flex flex-col gap-5 mt-2xs">
            <Field
              v-slot="{ componentField, errorMessage }"
              :name="`inverters[${index}].serialNumber`"
            >
              <SolInputText
                :id="`inverter-serial-number-${index}`"
                label="Número de série"
                v-bind="componentField"
                :error="errorMessage"
                :class="!!errorMessage ? 'error' : ''"
              />
            </Field>
          </div>

          <SolButton
            v-if="invertersModel && invertersModel?.length > 1 && index !== 0"
            id="remove-inverter"
            size="small"
            variant="tertiary"
            @click="removeInverter(index)"
          >
            <template #icon:left>
              <DeleteIcon class="icon-size-small text-feedback-negative-pure" />
            </template>
            <p class="text-feedback-negative-pure">
              {{ t('inversorInstallation.form.removeInverter') }}
            </p>
          </SolButton>
        </div>

        <SolDivider thickness="x-small" orientation="horizontal" class="md:site:hidden" />

        <SolButton
          id="add-inverter"
          variant="secondary"
          size="medium"
          class="w-full md:site:w-fit-content"
          @click="pushInverter(EMPTY_INVERTER)"
        >
          <template #icon:left>
            <AddIcon class="icon-size-medium" />
          </template>
          {{ t('inversorInstallation.form.addInverter') }}
        </SolButton>
      </form>
    </div>

    <SolDivider thickness="x-small" orientation="horizontal" class="hidden md:site:block" />

    <div class="inverter-footer">
      <Tooltip
        v-if="hasPendingTAF"
        :text="t('inversorInstallation.form.taf_pending_tooltip')"
        position="top"
        width="300px"
      >
        <SolButton
          id="submit-inverter-data"
          variant="primary"
          size="medium"
          class="inverter-footer-button submit-button"
          :disabled="hasPendingTAF || isSubmitDisabled"
          :loading="isPending"
          @click="handleSubmit"
        >
          {{ t('inversorInstallation.form.sendInfo') }}
        </SolButton>
      </Tooltip>
      <SolButton
        v-else
        id="submit-inverter-data"
        variant="primary"
        size="medium"
        class="inverter-footer-button submit-button"
        :disabled="hasPendingTAF || isSubmitDisabled"
        :loading="isPending"
        @click="handleSubmit"
      >
        {{ t('inversorInstallation.form.sendInfo') }}
      </SolButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
.inverter-upload-mode {
  > .page-title {
    @apply font-highlight text-sm text-neutral-low-dark;
  }

  > .form-container {
    @apply flex flex-col gap-6;
    @apply py-2xs;

    @screen md:site {
      @apply flex-row;
      @apply py-xs;
    }

    > .image {
      @apply w-fit-content m-auto mt-0 p-2xs;
      @apply rounded-lg border-neutral-high-medium border-[1px];
      background: rgba(245, 245, 245, 0.50);
    }

    > .form {
      @apply flex flex-col gap-4;
      @apply mt-2xs;

      @screen md:site {
        @apply gap-6;
        @apply mt-0;
      }

      .inverter-item {

        .inverter-item-header {
          @apply flex flex-row items-center gap-2;

          .inverter-item-title {
            @apply text-2xs font-bold leading-xs whitespace-nowrap;
          }
        }

        > .inverter-upload-block {
          @apply flex flex-col gap-1;

          .inverter-upload-label {
            @apply flex flex-row items-center gap-1 text-3xs fonts-body-medium-bold;
          }
        }
      }
    }
  }

  > .inverter-footer {
    @apply w-full;
    @apply flex flex-col gap-2;

    @screen md:site {
      @apply flex-row justify-end;
      @apply mt-2xs;
    }

    > .inverter-footer-button {
      @apply w-full md:site:w-fit-content;

      &.submit-button {
        @apply order-1 md:site:order-2
      }
    }
  }
}

.error:deep(.sol-download){
  @apply border-1 border-feedback-negative-pure;
}

.brand-select-container {
  @apply flex flex-col gap-1;

  .brand-disclaimer {
    @apply text-3xs text-brand-primary-dark;

    .brand-disclaimer-link {
      @apply text-brand-primary-medium underline cursor-pointer;
    }
  }
}
</style>
